import { addMonths, format, startOfMonth, subMonths } from 'date-fns';
import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { PaginationArrowIcon, PlusIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import Loader from '../../../components/loader/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import { TeamHooks, useModalState } from '../../../hooks/index';
import { PublicHoliday, Vacation, VacationView } from '../../../types/team.types';
import { DeleteConfirmModal } from '../delete-confirm-modal';
import { HolidayFormValues, VacationFormValues } from '../team.validation';
import { formatIsoDate } from '../team-date.util';
import { SelectField } from '../team-field';
import { CalendarView } from './calendar-view';
import { HolidayFormModal } from './holiday-form-modal';
import { ListView } from './list-view';
import { VacationFormModal } from './vacation-form-modal';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [-3, -2, -1, 0, 1, 2].map((offset) => CURRENT_YEAR + offset);

export const VacationsTab: FC = () => {
  const { members, isLoading: membersLoading } = TeamHooks.useTeamMembers();
  const { vacations, isLoading: vacationsLoading } = TeamHooks.useVacations();
  const { holidays, isLoading: holidaysLoading } = TeamHooks.usePublicHolidays();

  const { createVacation, isPending: isCreatingVacation } = TeamHooks.useCreateVacation();
  const { updateVacation, isPending: isUpdatingVacation } = TeamHooks.useUpdateVacation();
  const { deleteVacation, isPending: isDeletingVacation } = TeamHooks.useDeleteVacation();

  const { createHoliday, isPending: isCreatingHoliday } = TeamHooks.useCreatePublicHoliday();
  const { updateHoliday, isPending: isUpdatingHoliday } = TeamHooks.useUpdatePublicHoliday();
  const { deleteHoliday, isPending: isDeletingHoliday } = TeamHooks.useDeletePublicHoliday();

  const [view, setView] = useState<VacationView>(VacationView.CALENDAR);
  const [viewDate, setViewDate] = useState<Date>(startOfMonth(new Date()));
  const [personFilter, setPersonFilter] = useState('all');

  const [selectedVacation, setSelectedVacation] = useState<null | Vacation>(null);
  const [vacationToDelete, setVacationToDelete] = useState<null | Vacation>(null);
  const [selectedHoliday, setSelectedHoliday] = useState<null | PublicHoliday>(null);
  const [holidayToDelete, setHolidayToDelete] = useState<null | PublicHoliday>(null);

  const {
    isOpen: isVacationFormOpen,
    handleOpenModal: openVacationForm,
    handleCloseModal: closeVacationForm,
  } = useModalState({});
  const {
    isOpen: isHolidayFormOpen,
    handleOpenModal: openHolidayForm,
    handleCloseModal: closeHolidayForm,
  } = useModalState({});

  const handleAddVacation = () => {
    setSelectedVacation(null);
    openVacationForm();
  };

  const handleEditVacation = (vacation: Vacation) => {
    setSelectedVacation(vacation);
    openVacationForm();
  };

  const handleAddHoliday = () => {
    setSelectedHoliday(null);
    openHolidayForm();
  };

  const handleEditHoliday = (holiday: PublicHoliday) => {
    setSelectedHoliday(holiday);
    openHolidayForm();
  };

  const submitVacation = async (values: VacationFormValues) => {
    const payload = {
      teamMemberId: values.teamMemberId,
      startDate: values.startDate,
      endDate: values.endDate,
      notes: values.notes.trim() ? values.notes.trim() : null,
    };

    try {
      if (selectedVacation) {
        await updateVacation({ id: selectedVacation.id, ...payload });
        toast.success('Vacation updated');
      } else {
        await createVacation(payload);
        toast.success('Vacation added');
      }
      closeVacationForm();
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  const submitHoliday = async (values: HolidayFormValues) => {
    try {
      if (selectedHoliday) {
        await updateHoliday({ id: selectedHoliday.id, ...values });
        toast.success('Public holiday updated');
      } else {
        await createHoliday(values);
        toast.success('Public holiday added');
      }
      closeHolidayForm();
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  const confirmDeleteVacation = async () => {
    if (!vacationToDelete) return;

    try {
      await deleteVacation({ id: vacationToDelete.id });
      toast.success('Vacation deleted');
      setVacationToDelete(null);
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  const confirmDeleteHoliday = async () => {
    if (!holidayToDelete) return;

    try {
      await deleteHoliday({ id: holidayToDelete.id });
      toast.success('Public holiday deleted');
      setHolidayToDelete(null);
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  if (membersLoading || vacationsLoading || holidaysLoading) return <Loader />;

  const personOptions = [
    { label: 'All people', value: 'all' },
    ...members.map((member) => ({ label: member.name, value: member.id })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <RadioGroup
            className="h-fit w-fit divide-x divide-gray-200 overflow-hidden rounded border border-gray-200"
            onChange={(value: VacationView) => setView(value)}
            value={view}
          >
            {[VacationView.CALENDAR, VacationView.LIST].map((option) => (
              <Radio
                className="flex h-9 items-center justify-center px-5 text-sm font-[450] capitalize text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
                key={option}
                styleType="NONE"
                value={option}
              >
                {option}
              </Radio>
            ))}
          </RadioGroup>
          <SelectField
            className="w-[170px]"
            onChange={setPersonFilter}
            options={personOptions}
            value={personFilter}
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            className="flex h-9 w-fit items-center gap-1 whitespace-nowrap rounded border border-gray-200 bg-gray-25 px-4 py-[6px] text-sm font-[450] text-gray-700 hover:bg-gray-50"
            onClick={handleAddHoliday}
            styleType="NONE"
          >
            <PlusIcon className="size-4" iconColor="#344054" />
            Add Public Holiday
          </Button>
          <Button
            className="flex h-9 w-fit items-center gap-1 whitespace-nowrap rounded px-4 py-[6px] text-sm font-[550] text-white"
            onClick={handleAddVacation}
          >
            <PlusIcon className="size-4" iconColor="#ffffff" />
            Add Vacation
          </Button>
        </div>
      </div>

      {view === VacationView.CALENDAR ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
                <button
                  aria-label="Previous month"
                  className="flex size-9 items-center justify-center text-gray-600 transition-colors hover:bg-gray-50"
                  onClick={() => setViewDate((prev) => subMonths(prev, 1))}
                  type="button"
                >
                  <PaginationArrowIcon iconColor="#475467" />
                </button>
                <span className="min-w-[170px] border-x border-gray-200 px-4 py-2 text-center text-base font-[600] text-gray-800">
                  {format(viewDate, 'MMMM yyyy')}
                </span>
                <button
                  aria-label="Next month"
                  className="flex size-9 items-center justify-center text-gray-600 transition-colors hover:bg-gray-50"
                  onClick={() => setViewDate((prev) => addMonths(prev, 1))}
                  type="button"
                >
                  <PaginationArrowIcon className="rotate-180" iconColor="#475467" />
                </button>
              </div>
              <button
                className="flex h-9 items-center rounded-lg border border-gray-200 px-4 text-sm font-[450] text-gray-600 transition-colors hover:bg-gray-50"
                onClick={() => setViewDate(startOfMonth(new Date()))}
                type="button"
              >
                Today
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-[450] text-gray-500">Jump to year</span>
              <SelectField
                className="w-[100px]"
                onChange={(value) =>
                  setViewDate((prev) => new Date(Number(value), prev.getMonth(), 1))
                }
                options={YEAR_OPTIONS.map((year) => ({ label: String(year), value: String(year) }))}
                value={String(viewDate.getFullYear())}
              />
            </div>
          </div>

          <CalendarView
            holidays={holidays}
            members={members}
            onEditHoliday={handleEditHoliday}
            onEditVacation={handleEditVacation}
            personFilter={personFilter}
            vacations={vacations}
            viewDate={viewDate}
          />

          <div className="flex flex-wrap items-center gap-4 text-xs font-[450] text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-5 rounded bg-brand-700" /> Name chip = person on vacation
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-5 rounded bg-gray-500" /> Flag bar = public holiday (everyone)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-5 rounded bg-gray-100" /> Greyed = weekend (non-working)
            </span>
          </div>
        </>
      ) : (
        <ListView
          members={members}
          onDeleteVacation={setVacationToDelete}
          onEditVacation={handleEditVacation}
          personFilter={personFilter}
          vacations={vacations}
        />
      )}

      <VacationFormModal
        defaultMemberId={personFilter === 'all' ? undefined : personFilter}
        isOpen={isVacationFormOpen}
        isPending={isCreatingVacation || isUpdatingVacation}
        members={members}
        onDelete={() => {
          closeVacationForm();
          setVacationToDelete(selectedVacation);
        }}
        onDismiss={closeVacationForm}
        onSubmit={submitVacation}
        vacation={selectedVacation}
      />

      <HolidayFormModal
        holiday={selectedHoliday}
        isOpen={isHolidayFormOpen}
        isPending={isCreatingHoliday || isUpdatingHoliday}
        onDelete={() => {
          closeHolidayForm();
          setHolidayToDelete(selectedHoliday);
        }}
        onDismiss={closeHolidayForm}
        onSubmit={submitHoliday}
      />

      <DeleteConfirmModal
        description={
          vacationToDelete
            ? `Delete ${vacationToDelete.teamMember?.name ?? 'this'}'s vacation (${formatIsoDate(
                vacationToDelete.startDate,
              )} – ${formatIsoDate(vacationToDelete.endDate)})? This cannot be undone.`
            : ''
        }
        isOpen={!!vacationToDelete}
        isPending={isDeletingVacation}
        onConfirm={confirmDeleteVacation}
        onDismiss={() => setVacationToDelete(null)}
        title="Delete vacation"
      />

      <DeleteConfirmModal
        description={
          holidayToDelete
            ? `Delete the public holiday "${holidayToDelete.name}" on ${formatIsoDate(
                holidayToDelete.date,
              )}? This cannot be undone.`
            : ''
        }
        isOpen={!!holidayToDelete}
        isPending={isDeletingHoliday}
        onConfirm={confirmDeleteHoliday}
        onDismiss={() => setHolidayToDelete(null)}
        title="Delete public holiday"
      />
    </div>
  );
};
